import { FC, useEffect, useState } from "react";
import _ from "lodash";
import {
  Control,
  Controller,
  FieldValues,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import { MenuItem, Select } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useMutation } from "react-query";
import { MuiChipsInput } from "mui-chips-input";
import { fetchGetCompaniesByParent } from "../../organization-management/miscellaneous/fetchGetCompaniesByParent";
import { fetchGetAllParentCompanies } from "../../user-management/miscellaneous/fetchGetAllParentCompanies";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { SearchButton } from "../../common/components/SearchButton";
import { TasksListCriteria } from "../miscellaneous/tasksListSearchAtom";

type SearchTasksProps = {
  control: Control<FieldValues, any, FieldValues>;
  register: UseFormRegister<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues, FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  reset: UseFormReset<FieldValues>;
  setTasksSearch: any;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  defaultValues?: TasksListCriteria;
  onAtomReset?: () => void;
};

export const SearchTasks: FC<SearchTasksProps> = ({
  control,
  register,
  handleSubmit,
  getValues,
  setValue,
  reset,
  setTasksSearch,
  setPage,
  defaultValues,
  onAtomReset,
}) => {
  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];
  const isSuperAdmin = userRole[0]?.Name.toLowerCase() === "super admin";
  const isRoleAdmin = userRole[0]?.Name.toLowerCase() === "admin";
  const callerParentCompanyId = localStorage.getItem("parentCompanyId") ?? "";
  const callerParentCompanyName = localStorage.getItem("parentCompanyName") ?? "";

  const [companiesName, setCompaniesName] = useState([]);
  const [parentCompanies, setParentCompanies] = useState([]);
  const [chips, setChips] = useState<string[]>(defaultValues?.jobTitles ?? []);

  const [searchUrl, setSearchUrl] = useState({
    parentCompanyId: defaultValues?.parentCompanyId ?? "",
    companyId: defaultValues?.companyId ?? "",
    jobTitles: defaultValues?.jobTitles ?? [] as string[],
  });

  const mutationGetCompanies = useMutation(fetchGetCompaniesByParent, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companyName = _.map(res.data.data, (item) => {
          return { name: item.companyName, value: item.id };
        });
        setCompaniesName(companyName as any);
      }
    },
    onError: () => {},
  });

  const mutationGetParentCompanies = useMutation(fetchGetAllParentCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const data = _.map(res.data.data ?? [], (item: any) => ({
          name: item.companyName,
          value: item.id,
        }));
        setParentCompanies(data as any);
      }
    },
    onError: () => {},
  });

  const handleChange = (newChips: any) => {
    setChips(newChips);
    setSearchUrl((prev) => ({ ...prev, jobTitles: newChips }));
  };

  const handleParentCompanyChange = (parentCompanyId: string) => {
    setSearchUrl({ ...searchUrl, parentCompanyId, companyId: "" });
    setValue("organization", "");
    setChips([]);
    mutationGetCompanies.mutate(parentCompanyId || undefined);
  };

  const handleReset = () => {
    reset({});
    setPage(0);
    setChips([]);
    setSearchUrl({ parentCompanyId: "", companyId: "", jobTitles: [] });
    setTasksSearch({ parentCompanyId: "", companyId: "", jobTitles: [] });
    mutationGetCompanies.mutate(undefined);
    onAtomReset?.();
  };

  const onSearchSubmit = (data: any) => {
    setPage(0);
    const payload = {
      parentCompanyId: searchUrl.parentCompanyId ?? "",
      companyId: data.organization ?? searchUrl.companyId ?? "",
      jobTitles: chips,
    };

    setTasksSearch(payload);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      mutationGetParentCompanies.mutate();
      if (defaultValues?.parentCompanyId) {
        mutationGetCompanies.mutate(defaultValues.parentCompanyId);
      } else {
        mutationGetCompanies.mutate(undefined);
      }
    } else if (isRoleAdmin) {
      setParentCompanies([{ value: callerParentCompanyId, name: callerParentCompanyName }] as any);
      const parentId = defaultValues?.parentCompanyId || callerParentCompanyId;
      setSearchUrl((prev) => ({ ...prev, parentCompanyId: parentId }));
      setTasksSearch((prev: any) => ({ ...prev, parentCompanyId: parentId }));
      mutationGetCompanies.mutate(parentId);
    } else {
      mutationGetCompanies.mutate(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-9 border-2 pt-7 pb-7">
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <div className="flex flex-row justify-center items-center gap-8">
          {(isSuperAdmin || isRoleAdmin) && (
            <div className="flex flex-row justify-center items-center">
              <label className="font-bold mr-3">Company</label>
              <Select
                id="parentCompanyId"
                value={searchUrl.parentCompanyId ?? ""}
                onChange={(e) => handleParentCompanyChange(e.target.value)}
                style={{ height: "38px", width: "220px" }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {(parentCompanies ?? []).map((item: any) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}
          <div className="flex flex-row justify-center items-center">
            <label className="font-bold mr-3">Organization</label>
            <Controller
              control={control}
              name="organization"
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="organization"
                  value={searchUrl.companyId ?? value}
                  onChange={(e) => {
                    setValue("organization", e.target.value);
                    setSearchUrl({ ...searchUrl, companyId: e.target.value });
                  }}
                  style={{ height: "38px", width: "220px" }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {(companiesName ?? []).map((item: any) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            ></Controller>
          </div>

          <div className="flex flex-row justify-center items-center">
            <label className="font-bold mr-3">Job Title</label>
            <MuiChipsInput
              value={chips}
              onChange={handleChange}
              sx={{
                "& .MuiInputBase-input": {
                  height: "5px",
                },
              }}
            />
          </div>
        </div>

        <div className="flex flex-center justify-center mt-10 gap-5">
          <ButtonCancelA
            name="Reset"
            onHandleClick={handleReset}
            icon={<ClearIcon />}
          />
          <SearchButton />
        </div>
      </form>
    </div>
  );
};
